import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import SectionHeading from '@/components/base/SectionHeading';
import { catalog } from '@/mocks/catalog';
import { defaultReviews, reviews } from '@/mocks/reviews';
import usePageMeta from '@/hooks/usePageMeta';
import { formatViews, ratingLabel } from '@/utils/format';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const [toast, setToast] = useState(false);

  const item = catalog.find((entry) => entry.id === id);

  usePageMeta(
    item ? `${item.title}详情介绍 - 快活影院` : '影视详情 - 快活影院',
    item
      ? `${item.title}（${item.year}）由${item.director}执导，${item.cast.join('、')}主演。${item.summary}`
      : '快活影院影视详情页。',
    item ? `${item.title},${item.genres.join(',')},${item.year},快活影院` : undefined,
  );

  const related = useMemo(() => {
    if (!item) return [];
    return catalog
      .filter((entry) => entry.id !== item.id)
      .map((entry) => {
        let score = 0;
        if (entry.type === item.type) score += 1;
        if (entry.director === item.director) score += 3;
        score += entry.genres.filter((g) => item.genres.includes(g)).length * 2;
        score += entry.cast.filter((c) => item.cast.includes(c)).length * 2;
        return { entry, score };
      })
      .sort((a, b) => b.score - a.score || b.entry.rating - a.entry.rating)
      .slice(0, 6)
      .map((row) => row.entry);
  }, [item]);

  const selectedReviews = useMemo(() => {
    if (!item) return [];
    const matched = reviews.filter((review) => review.itemId === item.id);
    return matched.length > 0 ? matched : defaultReviews;
  }, [item]);

  if (!item) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-24 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading font-bold text-xl text-foreground-950">
          影视内容不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          你访问的作品可能已经下架，或链接有误。可以回到频道看看其他内容。
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Link
            to="/category/movie"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            浏览电影频道
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const typeLabel = item.type === 'movie' ? '电影' : '电视剧';

  return (
    <div className="w-full pb-4">
      {/* hero */}
      <div className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={item.backdrop}
          alt={`${item.title}剧照`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-950/60"></div>
        <div className="relative max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <nav aria-label="面包屑" className="text-sm text-background-200/80">
            <ol className="flex items-center gap-1.5 flex-wrap">
              <li><Link to="/" className="hover:text-accent-400 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li>
                <Link to={`/category/${item.type}`} className="hover:text-accent-400 transition-colors">
                  {typeLabel}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-background-50">{item.title}</li>
            </ol>
          </nav>

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-800 border border-background-50/10">
                <img
                  src={item.poster}
                  alt={`${item.title}海报`}
                  title={`${item.title} - ${item.genres.join(' ')}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-md bg-accent-500 text-foreground-950 text-xs font-semibold">
                  {typeLabel}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-50/10 text-background-100 text-xs">
                  {item.region}
                </span>
              </div>
              <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-background-50">
                {item.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400"></i>
                  <strong className="font-label text-accent-400 text-base">{ratingLabel(item.rating)}</strong>
                  <span className="text-background-300">/ 10 分</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {item.year} 年
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {item.duration}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line"></i>
                  热度 {formatViews(item.views)}
                </span>
              </div>

              <dl className="mt-5 space-y-2 text-sm">
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-background-300">导演</dt>
                  <dd className="text-background-50">{item.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-background-300">主演</dt>
                  <dd className="text-background-50">{item.cast.join('、')}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-background-300">类型</dt>
                  <dd className="text-background-50">{item.genres.join(' / ')}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setToast(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-semibold transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  在线观看（演示占位）
                </button>
                <Link
                  to={`/category/${item.type}?genre=${encodeURIComponent(item.genres[0])}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-50/30 hover:bg-background-50/10 text-background-50 text-sm transition-colors whitespace-nowrap"
                >
                  <i className="ri-price-tag-3-line"></i>
                  同类{item.genres[0]}作品
                </Link>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-50/10 text-background-200 text-xs"
                  >
                    # {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 lg:gap-10">
          <div className="space-y-10">
            {/* synopsis */}
            <section aria-label="内容简介">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                内容简介
              </h2>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <p className="text-sm md:text-[15px] leading-7 text-foreground-700">{item.synopsis}</p>
                <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap gap-3 text-sm">
                  {item.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${item.type}?genre=${encodeURIComponent(genre)}`}
                      className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            {/* reviews */}
            <section aria-label="观影指南与评论">
              <SectionHeading
                title="观影指南与评论精选"
                subtitle="来自观众与编辑部的示例观点，仅供参考"
                icon="ri-chat-quote-line"
              />
              <div className="space-y-4">
                {selectedReviews.map((review) => (
                  <article
                    key={review.id}
                    className="flex gap-4 p-4 md:p-5 rounded-lg border border-background-200 bg-background-50"
                  >
                    <span className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shrink-0 font-heading font-bold">
                      {review.avatarSeed}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-foreground-950">{review.author}</h3>
                        <span className="flex items-center gap-0.5 text-accent-600 text-xs font-label font-bold">
                          <i className="ri-star-fill text-[10px]"></i>
                          {review.rating}.0
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-7 text-foreground-700">{review.content}</p>
                    </div>
                  </article>
                ))}
              </div>
              <p className="mt-4 text-xs text-foreground-400">
                * 以上评论为演示用示例内容，用于展示页面结构，不代表真实观众评价。
              </p>
            </section>

            {/* related */}
            <section aria-label="相关推荐">
              <SectionHeading
                title="相关影视推荐"
                subtitle="根据类型、导演与演员为你匹配"
                icon="ri-magic-line"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                {related.map((entry) => (
                  <MovieCard key={entry.id} item={entry} />
                ))}
              </div>
            </section>
          </div>

          {/* sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
            <div className="rounded-lg border border-background-200 bg-background-100 p-4">
              <h3 className="font-heading font-bold text-base text-foreground-950 mb-3">作品速览</h3>
              <dl className="space-y-2.5 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">上映年份</dt>
                  <dd className="text-foreground-900">{item.year}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">地区</dt>
                  <dd className="text-foreground-900">{item.region}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">片长 / 集数</dt>
                  <dd className="text-foreground-900 text-right">{item.duration}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">评分</dt>
                  <dd className="text-foreground-900">{ratingLabel(item.rating)} 分</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-4">
              <h3 className="font-heading font-bold text-base text-foreground-950 mb-3">观看提示</h3>
              <p className="text-sm leading-6 text-foreground-600">
                本站为影视内容展示演示站点，仅提供作品资料与资讯浏览，不提供在线播放、下载与会员服务。
              </p>
              <button
                type="button"
                onClick={() => setToast(true)}
                className="mt-4 w-full py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                了解详情
              </button>
            </div>

            <Link
              to={`/category/${item.type}`}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{typeLabel}频道
            </Link>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="关闭提示"
            onClick={() => setToast(false)}
            className="absolute inset-0 bg-background-950/50"
          />
          <div className="relative w-full max-w-sm rounded-lg bg-background-50 p-6 text-center">
            <span className="w-12 h-12 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-information-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">暂不提供在线播放</h2>
            <p className="mt-2 text-sm text-foreground-600">
              快活影院为内容展示演示站点，本按钮为前端占位交互，未接入任何播放接口。
            </p>
            <button
              type="button"
              onClick={() => setToast(false)}
              className="mt-5 w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}